import { useEffect, useState } from "react";
import { Hammer, Wrench, Cog, Ruler, Paintbrush, Plug, Key, Scissors, Drill, Shovel, Axe, Flashlight } from "lucide-react";

const tools = [
  { Icon: Hammer, top: "8%", left: "6%", size: 76, dur: 9, delay: 0, depth: 0.06 },
  { Icon: Wrench, top: "18%", left: "88%", size: 92, dur: 11, delay: 1.2, depth: 0.12 },
  { Icon: Cog, top: "30%", left: "3%", size: 84, dur: 13, delay: 0.6, depth: 0.09 },
  { Icon: Ruler, top: "42%", left: "93%", size: 68, dur: 10, delay: 2, depth: 0.16 },
  { Icon: Paintbrush, top: "54%", left: "7%", size: 64, dur: 12, delay: 1.5, depth: 0.07 },
  { Icon: Plug, top: "64%", left: "90%", size: 76, dur: 9.5, delay: 0.3, depth: 0.14 },
  { Icon: Key, top: "26%", left: "48%", size: 56, dur: 14, delay: 2.4, depth: 0.2 },
  { Icon: Scissors, top: "76%", left: "42%", size: 60, dur: 11.5, delay: 1, depth: 0.1 },
  { Icon: Drill, top: "86%", left: "12%", size: 72, dur: 10.5, delay: 0.8, depth: 0.18 },
  { Icon: Shovel, top: "12%", left: "70%", size: 62, dur: 12.5, delay: 1.8, depth: 0.08 },
  { Icon: Axe, top: "92%", left: "75%", size: 66, dur: 9.8, delay: 0.4, depth: 0.15 },
  { Icon: Flashlight, top: "60%", left: "55%", size: 52, dur: 13.5, delay: 2.2, depth: 0.22 },
];

export default function AnimatedBackground() {
  const [glow, setGlow] = useState({ x: -600, y: -600 });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    let raf = null;
    const onMove = (e) => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        setGlow({ x: e.clientX, y: e.clientY });
        raf = null;
      });
    };
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const tilt = typeof window !== "undefined" && glow.x > 0 ? (glow.x / window.innerWidth - 0.5) * 10 : 0;

  return (
    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true" data-testid="animated-background">
      {/* dark workshop base with subtle depth vignette */}
      <div className="absolute inset-0 bg-[#232A31]" />
      <div
        className="absolute inset-0"
        style={{ background: "radial-gradient(ellipse at 50% 20%, rgba(70,82,94,0.35) 0%, transparent 60%)" }}
      />
      {/* dark blueprint grid */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.045) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />
      {/* steel tools drifting with parallax + cursor tilt */}
      {tools.map(({ Icon, top, left, size, dur, delay, depth }, i) => (
        <div
          key={i}
          className="absolute transition-transform duration-300 ease-out"
          style={{ top, left, transform: `translateY(${-scrollY * depth}px) rotate(${tilt * depth * 6}deg)` }}
        >
          <div className="mc-float" style={{ animationDuration: `${dur}s`, animationDelay: `${delay}s` }}>
            <Icon style={{ width: size, height: size }} className="text-[#94A3B8] opacity-[0.16] drop-shadow-[0_2px_3px_rgba(0,0,0,0.5)]" strokeWidth={1.1} />
          </div>
        </div>
      ))}
      {/* cursor-following worklight glow */}
      <div
        className="absolute w-[560px] h-[560px] rounded-full transition-transform duration-150 ease-out"
        style={{
          transform: `translate(${glow.x - 280}px, ${glow.y - 280}px)`,
          background: "radial-gradient(circle, rgba(247,197,0,0.14) 0%, rgba(212,105,30,0.08) 40%, transparent 70%)",
        }}
      />
    </div>
  );
}
